import { api } from "@/lib/api/client";
import type {
  TextSignListResponse,
  TextSignDetail,
  TextSignTrailRow,
  TextSignStats,
  Annotation,
} from "@pkhoffice/shared";

type Params = Record<string, string | number | undefined>;

const listFetcher =
  (path: string) =>
  async (p: Params = {}): Promise<TextSignListResponse> => {
    const { data } = await api.get(`/text-sign/${path}`, { params: p });
    return data.data ?? data;
  };

// 6 menus matching OLD PiOffice "Văn bản Ký điện tử"
export const fetchTrinhKy = listFetcher("trinh-ky");
export const fetchKyDuyet = listFetcher("ky-duyet");
export const fetchKyNhay = listFetcher("ky-nhay");
export const fetchBanHanh = listFetcher("ban-hanh");
export const fetchThaoLuan = listFetcher("thao-luan");
export const fetchDonVi = listFetcher("don-vi");

// Legacy aliases used by dashboard/stat cards
export const fetchInbox = fetchKyDuyet;
export const fetchDrafts = fetchTrinhKy;
export const fetchSent = fetchTrinhKy;
export const fetchPublished = fetchBanHanh;
export const fetchOrg = fetchDonVi;
export const fetchReadyPublish = fetchBanHanh;

export async function fetchTextSign(id: number): Promise<TextSignDetail> {
  const { data } = await api.get(`/text-sign/${id}`);
  return data.data ?? data;
}

export async function fetchTrail(id: number): Promise<TextSignTrailRow[]> {
  const { data } = await api.get(`/text-sign/${id}/trail`);
  return data.data ?? data;
}

export async function fetchMyStats(): Promise<TextSignStats> {
  const { data } = await api.get("/text-sign/my-stats");
  return data.data ?? data;
}

export async function fetchDocumentTypes(): Promise<{ typeId: number; name: string | null }[]> {
  const { data } = await api.get("/text-sign/document-types");
  return data.data ?? data;
}

export async function fetchAreas(): Promise<{ areaId: number; name: string | null }[]> {
  const { data } = await api.get("/text-sign/areas");
  return data.data ?? data;
}

export async function fetchPriorities(): Promise<{ priorityId: number; name: string | null }[]> {
  const { data } = await api.get("/text-sign/priorities");
  return data.data ?? data;
}

export async function fetchSecurityTypes(): Promise<{ stypeId: number; name: string | null }[]> {
  const { data } = await api.get("/text-sign/security-types");
  return data.data ?? data;
}

export async function fetchOrgs(
  q?: string,
): Promise<{ orgId: number; name: string | null; code: string | null }[]> {
  const { data } = await api.get("/text-sign/orgs", { params: { q } });
  return data.data ?? data;
}

export async function fetchSigners(params: {
  orgId?: number;
  role?: number;
  q?: string;
}): Promise<{ staffId: number; loginName: string; fullName: string }[]> {
  const { data } = await api.get("/text-sign/signers", { params });
  return data.data ?? data;
}

export async function submitText(body: unknown): Promise<{ textId: number }> {
  const { data } = await api.post("/text-sign/submit", body);
  return data.data ?? data;
}

export async function updateDraftText(textId: number, body: unknown): Promise<{ textId: number }> {
  const { data } = await api.post(`/text-sign/${textId}/update`, body);
  return data.data ?? data;
}

export async function uploadSignFile(
  file: File,
  opts: { convert?: boolean } = {},
): Promise<{ fileId: number; fileName: string; size: number; converted?: boolean }> {
  const form = new FormData();
  form.append("file", file);
  if (opts.convert) form.append("convert", "1");
  const { data } = await api.post("/file/upload", form);
  return data.data ?? data;
}

export async function fetchAnnotations(textId: number): Promise<Annotation[]> {
  const { data } = await api.get(`/text-sign/${textId}/annotations`);
  return data.data ?? data;
}

export async function saveAnnotations(
  textId: number,
  items: Omit<Annotation, "id">[],
): Promise<{ count: number }> {
  const { data } = await api.put(`/text-sign/${textId}/annotations`, { items });
  return data.data ?? data;
}
